/**
 * useStatementFilters.js
 * Custom hook for managing statement filtering, searching, and sorting
 */

import { useState, useMemo } from 'react';
import {
  processStatementsData,
  filterStatements,
  sortStatements,
  calculateStatementStats,
} from './statementUtils';

export const useStatementFilters = (statements) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [sortOrder, setSortOrder] = useState('newest');

  // Process statements data safely
  const processedStatements = useMemo(() => processStatementsData(statements), [statements]);

  // Apply filters and sorting
  const filteredStatements = useMemo(() => {
    let filtered = filterStatements(processedStatements, searchTerm, filterTab);
    return sortStatements(filtered, sortOrder);
  }, [processedStatements, searchTerm, filterTab, sortOrder]);

  // Calculate statistics for filter tabs
  const stats = useMemo(() => calculateStatementStats(filteredStatements), [filteredStatements]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleFilterTabChange = (event, newValue) => {
    setFilterTab(newValue);
  };

  const handleSortToggle = () => {
    setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest');
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterTab(0);
    setSortOrder('newest');
  };

  return {
    // State
    searchTerm,
    filterTab,
    sortOrder,

    // Computed values
    filteredStatements,
    stats,

    // Handlers
    handleSearchChange,
    handleFilterTabChange,
    handleSortToggle,
    resetFilters,
  };
};
